import React, { useState, useEffect, useRef } from 'react';
import { TypeElement, TypeItem } from '@/lib/stores/typesStore';
import { Input } from '@/components/ui/InputDefault';
import { Button } from '@/components/ui/button';
import { Check, Link, Trash2 } from 'lucide-react';
import { LinkedValueSelector } from './LinkedValueSelector';
import { VariableValueType } from '../types';

interface InlineElementRowProps {
  element: TypeElement;
  valueType: VariableValueType;
  allVariables: TypeItem[];
  currentVariableId: string;
  onUpdate: (element: TypeElement) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export const InlineElementRow: React.FC<InlineElementRowProps> = ({
  element,
  valueType,
  allVariables,
  currentVariableId,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const [editData, setEditData] = useState<TypeElement>(element);
  const [isDirty, setIsDirty] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setEditData(element);
    setIsDirty(false);
  }, [element]);

  const hasLinkedValue = !!(editData.linkedVariableId && editData.linkedElementId);
  
  const linkedVariable = hasLinkedValue 
    ? allVariables.find(v => v.id === editData.linkedVariableId) 
    : null;
  const linkedElement = linkedVariable?.data?.find(e => e.id === editData.linkedElementId);

  const displayNumericValue = hasLinkedValue && linkedElement 
    ? linkedElement.numericValue 
    : editData.numericValue;
  const displayRangeMin = hasLinkedValue && linkedElement 
    ? linkedElement.rangeMin 
    : editData.rangeMin;
  const displayRangeMax = hasLinkedValue && linkedElement 
    ? linkedElement.rangeMax 
    : editData.rangeMax;
  const displayIncrement = hasLinkedValue && linkedElement 
    ? linkedElement.increment 
    : editData.increment;

  const handleChange = (updates: Partial<TypeElement>) => {
    const newData = { ...editData, ...updates };
    setEditData(newData);
    setIsDirty(true);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      onUpdate(newData);
      setIsDirty(false);
    }, 500);
  };

  const handleBlur = () => {
    if (isDirty) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      onUpdate(editData);
      setIsDirty(false);
    }
  };

  const handleLinkedValueChange = (varId: string | undefined, elemId: string | undefined) => {
    const newData = { 
      ...editData, 
      linkedVariableId: varId, 
      linkedElementId: elemId 
    };
    setEditData(newData);
    onUpdate(newData);
  };

  return (
    <div className="flex items-center px-3 py-2 hover:bg-muted/30 group">
      <div className="flex-1 min-w-[120px] px-2">
        <Input
          value={editData.name}
          onChange={(e) => handleChange({ name: e.target.value })}
          onBlur={handleBlur}
          disabled={disabled}
          className="h-8 border-transparent bg-transparent hover:border-input focus:border-input disabled:opacity-100"
        />
      </div>

      {valueType === 'number' && (
        <div className="flex-1 min-w-[80px] px-2">
          {hasLinkedValue ? (
            <div className="h-8 flex items-center px-3 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
              <Link className="h-3 w-3 mr-2 shrink-0" />
              <span>{displayNumericValue !== null && displayNumericValue !== undefined ? displayNumericValue : '-'}</span>
            </div>
          ) : (
            <Input
              type="number"
              value={editData.numericValue ?? ""}
              onChange={(e) => handleChange({ 
                numericValue: e.target.value ? Number(e.target.value) : undefined 
              })}
              onBlur={handleBlur}
              disabled={disabled}
              placeholder="-"
              className="h-8 border-transparent bg-transparent hover:border-input focus:border-input disabled:opacity-100"
            />
          )}
        </div>
      )}

      {valueType === 'percentage' && (
        <div className="flex-1 min-w-[80px] px-2">
          {hasLinkedValue ? (
            <div className="h-8 flex items-center px-3 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
              <Link className="h-3 w-3 mr-2 shrink-0" />
              <span>{displayNumericValue !== null && displayNumericValue !== undefined ? displayNumericValue : '-'}%</span>
            </div>
          ) : (
            <div className="relative">
              <Input
                type="number"
                value={editData.numericValue ?? ""}
                onChange={(e) => handleChange({ 
                  numericValue: e.target.value ? Number(e.target.value) : undefined 
                })}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder="-"
                className="h-8 border-transparent bg-transparent hover:border-input focus:border-input pr-6 disabled:opacity-100"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
          )}
        </div>
      )}

      {valueType === 'boolean' && (
        <div className="flex-1 min-w-[80px] px-2">
          {hasLinkedValue ? (
            <div className="h-8 flex items-center px-3 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
              <Link className="h-3 w-3 mr-2 shrink-0" />
              <span>{displayNumericValue === 1 ? 'TRUE' : 'FALSE'}</span>
            </div>
          ) : (
            <select
              value={editData.numericValue === 1 ? 'true' : 'false'}
              onChange={(e) => handleChange({ 
                numericValue: e.target.value === 'true' ? 1 : 0 
              })}
              onBlur={handleBlur}
              disabled={disabled}
              className="h-8 w-full px-2 border-transparent bg-transparent hover:border-input focus:border-input disabled:opacity-100 text-sm"
            >
              <option value="false">FALSE</option>
              <option value="true">TRUE</option>
            </select>
          )}
        </div>
      )}

      {valueType === 'range' && (
        <>
          <div className="flex-1 min-w-[60px] px-2">
            {hasLinkedValue ? (
              <div className="h-8 flex items-center px-2 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
                <Link className="h-3 w-3 mr-1 shrink-0" />
                <span>{displayRangeMin !== null && displayRangeMin !== undefined ? displayRangeMin : '-'}</span>
              </div>
            ) : (
              <Input
                type="number"
                value={editData.rangeMin ?? ""}
                onChange={(e) => handleChange({ 
                  rangeMin: e.target.value ? Number(e.target.value) : undefined 
                })}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder="-"
                className="h-8 border-transparent bg-transparent hover:border-input focus:border-input disabled:opacity-100"
              />
            )}
          </div>
          <div className="flex-1 min-w-[60px] px-2">
            {hasLinkedValue ? (
              <div className="h-8 flex items-center px-2 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
                <span>{displayRangeMax !== null && displayRangeMax !== undefined ? displayRangeMax : '-'}</span>
              </div>
            ) : (
              <Input
                type="number"
                value={editData.rangeMax ?? ""}
                onChange={(e) => handleChange({ 
                  rangeMax: e.target.value ? Number(e.target.value) : undefined 
                })}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder="-"
                className="h-8 border-transparent bg-transparent hover:border-input focus:border-input disabled:opacity-100"
              />
            )}
          </div>
          <div className="flex-1 min-w-[60px] px-2">
            {hasLinkedValue ? (
              <div className="h-8 flex items-center px-2 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
                <span>{displayIncrement !== null && displayIncrement !== undefined ? displayIncrement : '-'}</span>
              </div>
            ) : (
              <Input
                type="number"
                value={editData.increment ?? ""}
                onChange={(e) => handleChange({ 
                  increment: e.target.value ? Number(e.target.value) : undefined 
                })}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder="-"
                className="h-8 border-transparent bg-transparent hover:border-input focus:border-input disabled:opacity-100"
              />
            )}
          </div>
        </>
      )}

      <div className="flex-1 min-w-[120px] px-2">
        {disabled ? (
          <div className="h-8 flex items-center text-sm text-muted-foreground">
            {linkedVariable && linkedElement ? (
              <span className="truncate">
                {linkedVariable.name}.{linkedElement.name}
              </span>
            ) : (
              <span>-</span>
            )}
          </div>
        ) : (
          <LinkedValueSelector
            variables={allVariables}
            currentVariableId={currentVariableId}
            linkedVariableId={editData.linkedVariableId}
            linkedElementId={editData.linkedElementId}
            onSelect={handleLinkedValueChange}
          />
        )}
      </div>

      <div className="flex-1 min-w-[120px] px-2">
        <Input
          value={editData.description || ""}
          onChange={(e) => handleChange({ description: e.target.value })}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder="-"
          className="h-8 border-transparent bg-transparent hover:border-input focus:border-input disabled:opacity-100"
        />
      </div>

      <div className="w-12 shrink-0 px-2 flex justify-center">
        {isDirty ? (
          <Check className="h-4 w-4 text-muted-foreground animate-pulse" />
        ) : !disabled ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
};
